import React from 'react';
import { FaTimes } from '@react-icons/all-files/fa/FaTimes';
import ProfaseModal from '../commons/ProfaseModal';
import { formatGrupoEmpresarialLabel, getKanbanEmpresa } from './kanban-task-context';
import ActividadAdjuntosSection from './ActividadAdjuntosSection';

export interface ActividadAdjuntosListModalContext {
  actividad?: string;
  grupoEmpresarial?: string;
  rfc?: string;
  razonSocial?: string;
  usuarioAsignado?: string;
  usuarioAutoriza?: string;
}

export interface ActividadAdjuntosListModalProps {
  open: boolean;
  title: string;
  context?: ActividadAdjuntosListModalContext;
  actividadId?: string | number;
  nodoInstanciaId?: string | number;
  onClose: () => void;
}

function strVal(v: unknown): string {
  if (v == null) return '';
  return String(v).trim();
}

export function buildAdjuntosModalContextFromKanbanTask(
  task: { asunto: string; raw: unknown },
): ActividadAdjuntosListModalContext {
  const raw = (task.raw && typeof task.raw === 'object'
    ? task.raw
    : {}) as Record<string, unknown>;
  const data = raw.data as Record<string, unknown> | undefined;
  const empresa = getKanbanEmpresa(raw);

  const actividad = strVal(
    (raw.actividad as Record<string, unknown> | undefined)?.nombre
    ?? raw.actividadNombre
    ?? raw.categoriaActividadDescripcion
    ?? data?.categoriaActividadDescripcion,
  ) || task.asunto;

  const autorizaId = raw.autorizaId ?? data?.autorizaId;
  const usuarioAutoriza = autorizaId != null && autorizaId !== ''
    ? strVal(raw.autorizaNombreCompleto ?? data?.autorizaNombreCompleto) || undefined
    : undefined;

  return {
    actividad,
    grupoEmpresarial: empresa.grupoEmpresarial,
    rfc: empresa.rfc,
    razonSocial: empresa.razonSocial,
    usuarioAsignado: strVal(raw.usuarioNombreCompleto ?? data?.usuarioNombreCompleto) || undefined,
    usuarioAutoriza,
  };
}

export function buildAdjuntosModalContextFromEstadoNodosRow(
  row: Record<string, unknown>,
): ActividadAdjuntosListModalContext {
  const grupoEmpresarial = formatGrupoEmpresarialLabel({
    alias: row.grupoAlias,
    grpAliasDecomsa: row.grupoAliasDecomsa,
    grpAliasEquilibrio: row.grupoAliasEquilibrio,
  });

  const autorizaId = row.autorizaId;
  const usuarioAutoriza = autorizaId != null && autorizaId !== ''
    ? strVal(row.autorizaNombreCompleto) || undefined
    : undefined;

  return {
    actividad: strVal(row.categoriaActividadDescripcion) || undefined,
    grupoEmpresarial,
    rfc: strVal(row.empresaRfc) || undefined,
    razonSocial: strVal(row.empresaRazonSocial) || undefined,
    usuarioAsignado: strVal(row.usuarioNombreCompleto) || undefined,
    usuarioAutoriza,
  };
}

function ContextField({ label, value }: { label: string; value?: string }) {
  if (!value) return null;

  return (
    <div className="actividad-adjuntos-list-modal__field">
      <span className="actividad-adjuntos-list-modal__field-label">{label}</span>
      <span className="actividad-adjuntos-list-modal__field-value">{value}</span>
    </div>
  );
}

function ActividadAdjuntosListModalContextPanel({
  context,
}: {
  context?: ActividadAdjuntosListModalContext;
}) {
  if (!context) return null;

  const hasContent = Boolean(
    context.actividad
    || context.grupoEmpresarial
    || context.rfc
    || context.razonSocial
    || context.usuarioAsignado
    || context.usuarioAutoriza,
  );

  if (!hasContent) return null;

  return (
    <div className="actividad-adjuntos-list-modal__context">
      <ContextField label="Actividad" value={context.actividad} />
      <ContextField label="Grupo empresarial" value={context.grupoEmpresarial} />
      <div className="actividad-adjuntos-list-modal__row-2">
        <ContextField label="RFC" value={context.rfc} />
        <ContextField label="Razón social" value={context.razonSocial} />
      </div>
      <ContextField label="Usuario asignado" value={context.usuarioAsignado} />
      <ContextField label="Usuario autoriza" value={context.usuarioAutoriza} />
    </div>
  );
}

export default function ActividadAdjuntosListModal({
  open,
  title,
  context,
  actividadId,
  nodoInstanciaId,
  onClose,
}: ActividadAdjuntosListModalProps) {
  const titleId = 'actividad-adjuntos-list-modal-title';

  return (
    <ProfaseModal
      open={open}
      size="wide"
      titleId={titleId}
      dialogClassName="profase-modal-dialog--actividad-adjuntos-list"
      onBackdropMouseDown={e => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="actividad-adjuntos-list-modal">
        <div className="actividad-adjuntos-list-modal__header">
          <h2 id={titleId} className="profase-modal-title actividad-adjuntos-list-modal__title">
            {title}
          </h2>
          <button
            type="button"
            className="actividad-adjuntos-list-modal__close"
            onClick={onClose}
            title="Cerrar"
            aria-label="Cerrar"
          >
            <FaTimes size={14} />
          </button>
        </div>
        <ActividadAdjuntosListModalContextPanel context={context} />
        <div className="actividad-adjuntos-list-modal__body">
          <ActividadAdjuntosSection
            actividadId={actividadId}
            nodoInstanciaId={nodoInstanciaId}
            readOnly
          />
        </div>
      </div>
    </ProfaseModal>
  );
}
